// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,       setUser]       = useState(null);
  const [loading,    setLoading]    = useState(true);   // initial session check
  const [authReady,  setAuthReady]  = useState(false);

  // ── Restore session on mount ────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('ke_token');
    if (!token) { setLoading(false); setAuthReady(true); return; }

    authApi.getMe()
      .then(({ user }) => setUser(user))
      .catch(() => localStorage.removeItem('ke_token'))
      .finally(() => { setLoading(false); setAuthReady(true); });
  }, []);

  // ── Listen for forced logout (401 from axios interceptor) ───────────────
  useEffect(() => {
    const handle = () => { setUser(null); localStorage.removeItem('ke_token'); };
    window.addEventListener('ke:logout', handle);
    return () => window.removeEventListener('ke:logout', handle);
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    localStorage.setItem('ke_token', data.token);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (formData) => {
    const data = await authApi.register(formData);
    localStorage.setItem('ke_token', data.token);
    setUser(data.user);
    return data;
  }, []);

  // Passwordless phone login — completes the session from an OTP verify.
  const loginWithOtp = useCallback(async (phone, otp) => {
    const data = await authApi.otpVerify(phone, otp);
    localStorage.setItem('ke_token', data.token);
    setUser(data.user);
    return data;
  }, []);

  // Sign in with Google — completes the session from a GIS ID token.
  const loginWithGoogle = useCallback(async (credential) => {
    const data = await authApi.google(credential);
    localStorage.setItem('ke_token', data.token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch {}
    localStorage.removeItem('ke_token');
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    const res = await authApi.updateProfile(data);
    setUser(res.user);
    return res;
  }, []);

  const addAddress = useCallback(async (address) => {
    const res = await authApi.addAddress(address);
    setUser(prev => ({ ...prev, addresses: res.addresses }));
    return res;
  }, []);

  const deleteAddress = useCallback(async (id) => {
    const res = await authApi.deleteAddress(id);
    setUser(prev => ({ ...prev, addresses: res.addresses }));
    return res;
  }, []);

  // ── Wishlist helpers ────────────────────────────────────────────────────
  const toggleWishlist = useCallback(async (productId) => {
    const res = await authApi.toggleWishlist(productId);
    setUser(prev => ({ ...prev, wishlist: res.wishlist }));
    return res.action;   // 'added' | 'removed'
  }, []);

  const isWishlisted = useCallback((productId) => {
    if (!user?.wishlist) return false;
    return user.wishlist.some(id =>
      (typeof id === 'object' ? id._id : id)?.toString() === productId?.toString()
    );
  }, [user]);

  const value = {
    user,
    loading,
    authReady,
    isLoggedIn:  !!user,
    isAdmin:     user?.role === 'admin',
    login,
    loginWithOtp,
    loginWithGoogle,
    register,
    logout,
    updateProfile,
    addAddress,
    deleteAddress,
    toggleWishlist,
    isWishlisted,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/** Hook — throws if used outside AuthProvider */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
